import { FC, useContext, useEffect, useRef, useState } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderProfileFlyoutTable } from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileFlyoutTable";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { ProviderProfileFlyoutTableData } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileFlyoutTableInterfaces";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { AccessGapTypeEnumToDisplay, formatSpecialtyWithCode, GeoInfoToDisplay, RegionSetStatusToDisplay, regionSetStatusSortRank } from "@app/projects/network-assessment/provider-hub/utility";
import { AccessGapTypeEnum } from "@app/projects/network-assessment/provider-hub/enumerators";

interface KeyPointsFlyoutProps {
  gapType: AccessGapTypeEnum.PartiallyFillableGap | AccessGapTypeEnum.FillableBySingleProvider;
}

export const KeyPointsFlyout: FC<KeyPointsFlyoutProps> = (props: KeyPointsFlyoutProps) => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState<ProviderProfileFlyoutTableData[]>([]);
  const providerHubService = useRef(new ProviderHubService()).current;

  useEffect(() => {
    const apiCall = providerHubService.getProviderServicingAccessPointsByAccessGapTypeAsync(projectContext?.project?.projectId, providerHubContext.selectedProvider.projectRunConfigurationId, providerHubContext.selectedProvider.servicingProviderKey, props.gapType);

    Promise.resolve(apiCall).then((response) => {
      const sortedAccessPoints = [...response].sort(
        (a, b) => regionSetStatusSortRank(a.regionSetStatus) - regionSetStatusSortRank(b.regionSetStatus)
      );
      /* oxlint-disable sort-keys */
      const newTableData = sortedAccessPoints.map((r) => {
        let linkHref = `${window.customUIAbsolutePath}/projects/${projectContext.project.projectId}/adequacy`;
        if (!!r.countySSA)
          linkHref += `/county/${r.countySSA}`;
        if (!!r.specialtyGroupCode)
          linkHref += `/specialty/${r.specialtyGroupCode}`;

        return {
          [wafflesKeyColumnName]: r.servicingAccessPointId.toString(),
          serviceArea: `${isNullOrUndefined(r.countySSA) ? "---" : r.countySSA} ${isNullOrUndefined(r.countyName) ? "---" : r.countyName}, ${isNullOrUndefined(r.servicingState) ? "---" : r.servicingState}`,
          countyStatus: RegionSetStatusToDisplay(r.regionSetStatus),
          specialty: isNullOrUndefined(r.specialtyGroupName) ? "---" : formatSpecialtyWithCode({ code: r.specialtyGroupCode, name: r.specialtyGroupName }),
          address: (
            <div>
              {isNullOrUndefined(r.address) ? "---" : r.address} <br />
              {isNullOrUndefined(r.address2) ? null : <>{r.address2}<br /></>}
              {isNullOrUndefined(r.city) ? "---" : r.city}, {isNullOrUndefined(r.state) ? "---" : r.state} {isNullOrUndefined(r.zip) ? "---" : r.zip}
            </div>
          ),
          geoInfo: GeoInfoToDisplay(r.geoInfo),
          fillableGapIndicator: AccessGapTypeEnumToDisplay(r.gapType),
          servesPercent: r.servesPercent,
          impactPercent: r.impactPercent,
          gainsPercent: r.gainsPercent,
          passPercent: r.passPercent,
          projectLink: <a href={linkHref} target="_blank" rel="noreferrer">View in Adequacy <QesIcon iconName={QesIcons.ShareV4} isDecorative /></a>,
        }
      });
      /* oxlint-enable sort-keys */
      setLoading(false);
      setTableData(newTableData);
    });
  }, []);

  return (
    <>
      {loading ? <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="Loading" /> : <ProviderProfileFlyoutTable data={tableData} gapType={props.gapType} />}
    </>
  );
}
